import { useState } from 'react';
import Figura from './Figura';
import Visor from './Visor';
import useCarrusel from '../hooks/useCarrusel';
import { introServicios } from '../data/contenido';
import servicios from '../data/servicios';

const Flecha = ({ hacia }) => (
  <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden="true">
    <g
      stroke="currentColor"
      strokeWidth="1"
      transform={hacia === 'izq' ? 'translate(17 0) scale(-1 1)' : undefined}
    >
      <path d="M0 5.5h15.5" />
      <path d="M11.4 1.2 15.7 5.5l-4.3 4.3" />
    </g>
  </svg>
);

/**
 * Las fotos de un servicio cuando son pocas: una al lado de la otra.
 *
 * Con una sola queda igual que siempre, ocupando su mitad de la grilla. Con
 * dos o tres se reparten ese mismo ancho, que es lo que pidio Valentina:
 * verlas todas juntas sin tener que arrastrar nada.
 */
function Fila({ fotos, onMirar }) {
  return (
    <div className="servicio__fila" data-cuantas={Math.min(fotos.length, 4)}>
      {fotos.map((foto, i) => (
        <button
          type="button"
          className="servicio__foto"
          key={`${foto.src}-${i}`}
          onClick={() => onMirar(i)}
          aria-label={`Ver en grande: ${foto.alt}`}
        >
          <Figura foto={foto} retraso={Math.min(i, 3) * 90} />
        </button>
      ))}
    </div>
  );
}

/**
 * Y cuando son muchas, una tira que se arrastra.
 *
 * Reusa el mismo comportamiento que la galeria de abajo —el mismo arrastre,
 * las mismas flechas, el mismo visor— para que no haya dos carruseles que se
 * manejen distinto en la misma pagina.
 */
function Carrusel({ fotos, etiqueta, onMirar }) {
  const { tira, alInicio, alFinal, desplazar, huboArrastre } = useCarrusel();

  return (
    <div className="servicio__carrusel">
      <div className="tira tira--servicio" ref={tira} tabIndex={0} role="region" aria-label={etiqueta}>
        {fotos.map((foto, i) => (
          <button
            type="button"
            className="tira__item"
            key={`${foto.src}-${i}`}
            data-orientacion={foto.orientacion}
            onClick={() => {
              if (huboArrastre.current) return;
              onMirar(i);
            }}
            aria-label={`Ver en grande: ${foto.alt}`}
          >
            <Figura foto={foto} retraso={Math.min(i, 4) * 90} />
          </button>
        ))}
      </div>

      <div className="mandos mandos--servicio">
        <button
          type="button"
          className="mando"
          onClick={() => desplazar(-1)}
          disabled={alInicio}
          aria-label={`${etiqueta}: fotos anteriores`}
        >
          <Flecha hacia="izq" />
        </button>
        <button
          type="button"
          className="mando"
          onClick={() => desplazar(1)}
          disabled={alFinal}
          aria-label={`${etiqueta}: fotos siguientes`}
        >
          <Flecha hacia="der" />
        </button>
      </div>
    </div>
  );
}

function Servicio({ servicio, lado }) {
  const [mirando, setMirando] = useState(null);
  const { fotos, vista, titulo, texto, numero } = servicio;
  const muchas = vista === 'carrusel' && fotos.length > 1;

  return (
    /*
     * Con una sola foto, la linea va como siempre: foto de un lado, texto del
     * otro. Con varias, el texto va primero y las fotos debajo, a todo el
     * ancho: es lo que pidio Valentina —"primero la descripcion y que
     * aparezcan unas 3 o 4 fotos"— y ademas tres fotos apretadas en media
     * grilla no se verian.
     */
    <article
      className="servicio"
      data-lado={lado}
      data-fotos={fotos.length > 1 ? 'varias' : 'una'}
      data-vista={muchas ? 'carrusel' : 'fila'}
    >
      <div className="servicio__figura">
        {fotos.length > 0 &&
          (muchas ? (
            <Carrusel fotos={fotos} etiqueta={titulo} onMirar={setMirando} />
          ) : (
            <Fila fotos={fotos} onMirar={setMirando} />
          ))}
      </div>

      <div className="servicio__texto" data-reveal="" style={{ '--retraso': '120ms' }}>
        <p className="servicio__numero">{numero}</p>
        <h3 className="servicio__titulo">{titulo}</h3>
        {texto
          .split(/\n\s*\n/)
          .map((p) => p.trim())
          .filter(Boolean)
          .map((parrafo) => (
            <p key={parrafo.slice(0, 40)}>{parrafo}</p>
          ))}
      </div>

      <Visor
        fotos={fotos}
        indice={mirando}
        onCerrar={() => setMirando(null)}
        onIr={(paso) => setMirando((i) => i + paso)}
      />
    </article>
  );
}

export default function Servicios() {
  return (
    <section className="servicios" id="servicios">
      <div className="envoltura">
        <div className="cabecera" data-reveal="">
          <p className="rotulo">{introServicios.rotulo}</p>
          <h2 className="titulo">{introServicios.titulo}</h2>
          <p className="servicios__intro">{introServicios.texto}</p>
        </div>

        {servicios.map((servicio, i) => (
          <Servicio
            key={`${servicio.titulo}-${i}`}
            servicio={servicio}
            lado={i % 2 === 0 ? 'izq' : 'der'}
          />
        ))}
      </div>
    </section>
  );
}
